import React, { useEffect, useRef, useState } from 'react';
import { Plyr, APITypes } from 'plyr-react';
import 'plyr-react/plyr.css';
import { mediaProgressStore } from '@app/shared/services/media-progress-store';

// Static iron-grey theme for Plyr controls (volume/progress fills)
const PLYR_COLOR = '#4A4A4A';

export interface PlyrVideoProps {
	url: string;
	/** Identifier used to persist playback progress. Omit to disable saving. */
	progressKey?: string;
	isVertical?: boolean;
	/** CSS max-height for the wrapper. Defaults to '65vh' for non-vertical, 'unset' for vertical. */
	maxHeight?: string;
}

export const PlyrVideo: React.FC<PlyrVideoProps> = ({ url, progressKey, isVertical, maxHeight }) => {
	const plyrRef = useRef<APITypes>(null);
	const lastSavedRef = useRef(0);
	const wrapperRef = useRef<HTMLDivElement>(null);
	// Unique class per instance so the injected <style> scopes cleanly
	const scopeClassRef = useRef(`plyr-scope-${Math.random().toString(36).slice(2, 9)}`);

	const [verticalHeightPx, setVerticalHeightPx] = useState<number | null>(null);

	useEffect(() => {
		if (!isVertical) return;
		const el = wrapperRef.current;
		if (!el) return;
		const update = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setVerticalHeightPx(w * (16 / 9));
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		window.addEventListener('resize', update);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', update);
		};
	}, [isVertical]);

	useEffect(() => {
		const plyr = plyrRef.current?.plyr as any;
		if (!plyr || !plyr.on) return;

		const onReady = () => {
			if (!progressKey) return;
			const saved = mediaProgressStore.getVideo(progressKey);
			if (saved > 0) {
				try { plyr.currentTime = saved; } catch { /* ignore */ }
			}
		};
		const onTimeUpdate = () => {
			if (!progressKey) return;
			const current = plyr.currentTime;
			// Throttle persistence to ~1s intervals
			if (current > 0 && Math.abs(current - lastSavedRef.current) >= 1) {
				mediaProgressStore.setVideo(progressKey, current);
				lastSavedRef.current = current;
			}
		};
		const onEnded = () => {
			if (progressKey) mediaProgressStore.setVideo(progressKey, 0);
		};

		plyr.on('ready', onReady);
		plyr.on('timeupdate', onTimeUpdate);
		plyr.on('ended', onEnded);

		return () => {
			try {
				plyr.off('ready', onReady);
				plyr.off('timeupdate', onTimeUpdate);
				plyr.off('ended', onEnded);
			} catch { /* ignore teardown errors */ }
		};
	}, [progressKey, url]);

	return (
		<div
			ref={wrapperRef}
			className={`${isVertical ? 'vertical-config' : 'fragment'} ${scopeClassRef.current}`}
			style={{
				width: '100%',
				height: isVertical ? (verticalHeightPx != null ? `${verticalHeightPx}px` : undefined) : undefined,
				// Caller can pass an explicit maxHeight; otherwise let the wrapper grow to fit
				// Plyr's natural rendering so the next content always renders below the video,
				// never on top of it.
				maxHeight: maxHeight,
				position: 'relative',
				display: 'block',
			}}
		>
			<style
				dangerouslySetInnerHTML={{
					__html: `
						.${scopeClassRef.current} .plyr {
							--plyr-color-main: ${PLYR_COLOR};
							--plyr-range-fill-background: ${PLYR_COLOR};
							--plyr-video-range-fill-background: ${PLYR_COLOR};
							--plyr-audio-range-fill-background: ${PLYR_COLOR};
						}
						.${scopeClassRef.current} .plyr input[type="range"] {
							color: ${PLYR_COLOR} !important;
						}
						${isVertical ? `
						/* Pin Plyr to fill the wrapper (which has an explicit pixel height set from JS).
						   Disable Plyr's own padding-bottom aspect-ratio trick since the wrapper handles sizing. */
						.${scopeClassRef.current} .plyr {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							padding-bottom: 0 !important;
						}
						.${scopeClassRef.current} .plyr video {
							object-fit: contain;
						}
						` : ''}
					`,
				}}
			/>
			<Plyr
				ref={plyrRef}
				source={{
					type: 'video',
					sources: [{ src: url, provider: 'html5' }],
				}}
				options={{
					controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
					// Aspect ratio for vertical is enforced by the wrapper's explicit JS-measured height.
					// Plyr's `ratio` option uses padding-bottom on .plyr, which breaks in flex/grid parents.
				}}
			/>
		</div>
	);
};
